import { TextInput } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { logger } from "#app/monitoring/Logger";
import { colors } from "#app/theme/v2/colors";
import { fontSizes } from "#app/theme/v2/fontSizes";
import { fontWeights } from "#app/theme/v2/fontWeights";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { FileUpload } from "#components/molecules/FileUpload/FileUpload";
import { Modal } from "#components/molecules/Modal/Modal";
import { ClientSelect } from "#modules/clientsV2/view/organisms/ClientSelect";
import { useCreateQuoteRequestFiles } from "#modules/quoteRequestsV2/view/useCreateQuoteRequestFiles";
import { useUpdateQuoteRequestClient } from "#modules/quoteRequestsV2/view/useUpdateQuoteRequestClient";
import { useUpdateQuoteRequestReference } from "#modules/quoteRequestsV2/view/useUpdateQuoteRequestReference";

type Props = {
  opened: boolean;
  onClose: () => void;
  title: string;
  primaryText: string;
  quoteRequestId?: string;
  initialReference?: string;
  initialClientId?: string | null;
  showClientAndReferenceFields?: boolean;
  onNext: (quoteRequestId: string) => void | Promise<void>;
};

const inputStyles = {
  label: {
    fontSize: fontSizes[14],
    fontWeight: fontWeights.medium,
    color: colors.text.primaryDefault,
    lineHeight: fontSizes[20],
    marginBottom: spacing[1],
  },
  input: {
    background: colors.bg.elevation.primary,
    border: `1px solid ${colors.border.secondaryDefault}`,
    borderRadius: radius.xs,
    padding: spacing[2],
    fontSize: fontSizes[14],
    color: colors.text.primaryDefault,
    lineHeight: fontSizes[20],
    height: "auto",
    minHeight: spacing[9],
  },
};

export const QuoteRequestFilesModal = ({
  opened,
  onClose,
  title,
  primaryText,
  quoteRequestId: existingQuoteRequestId,
  initialReference = "",
  initialClientId = null,
  showClientAndReferenceFields = true,
  onNext,
}: Props) => {
  const { t } = useTranslation();

  const { files, addFiles, removeFile, quoteRequestId, isPending, reset, commitFiles } =
    useCreateQuoteRequestFiles(existingQuoteRequestId);

  const { mutateAsync: updateClientAsync } = useUpdateQuoteRequestClient();
  const { mutateAsync: updateReferenceAsync } = useUpdateQuoteRequestReference();

  const [reference, setReference] = useState(initialReference);
  const [clientId, setClientId] = useState<string | null>(initialClientId);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canProceed =
    !!quoteRequestId && !isPending && files.length > 0 && files.every((f) => f.progress === 100);

  const handleClose = () => {
    reset();
    setReference(initialReference);
    setClientId(initialClientId);
    onClose();
  };

  const handleNext = async () => {
    if (!quoteRequestId) {
      return;
    }
    setIsSubmitting(true);

    try {
      await commitFiles();
      if (reference !== initialReference) {
        await updateReferenceAsync({ id: quoteRequestId, reference });
      }
      if (clientId && clientId !== initialClientId) {
        await updateClientAsync({ id: quoteRequestId, clientId });
      }
    } catch (err) {
      logger.error(err);
    }

    try {
      await onNext(quoteRequestId);
    } catch (err) {
      logger.error(err);
    }

    setIsSubmitting(false);
    handleClose();
  };

  return (
    <Modal
      opened={opened}
      title={title}
      closeText={t("common.cancel")}
      primaryText={primaryText}
      onClose={handleClose}
      onPrimary={handleNext}
      primaryDisabled={!canProceed}
      primaryLoading={isSubmitting}
    >
      {showClientAndReferenceFields && (
        <>
          <ClientSelect value={clientId} onChange={setClientId} />
          <TextInput
            label={t("quoteRequestsV2.modal.referenceLabel")}
            placeholder={t("quoteRequestsV2.modal.referencePlaceholder")}
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            styles={inputStyles}
          />
        </>
      )}
      <FileUpload
        label={showClientAndReferenceFields ? t("quoteRequestsV2.modal.filesLabel") : undefined}
        files={files}
        onAdd={addFiles}
        onDelete={removeFile}
      />
    </Modal>
  );
};
